export type OrderCostGroup = "estimated" | "actual" | "additionalRevenue";

export type OrderCostReviewer = "sale" | "manager";

export type OrderCostReviewStatus =
  | "PENDING"
  | "APPROVED"
  | "REJECTED"
  | "REQUEST_EDIT";

export type OrderCostReviewPermissions = {
  canApprove: boolean;
  canReject: boolean;
};

export type OrderCostLine = {
  configId: string;
  key?: string;
  note?: string;
  children?: OrderCostLine[];
  touchedFields?: Record<string, boolean>;
  values?: Partial<
    Record<
      OrderCostGroup,
      {
        value?: number;
        editable?: boolean;
        locked?: boolean;
      }
    >
  >;
  reviews?: Partial<
    Record<
      OrderCostReviewer,
      {
        status?: OrderCostReviewStatus | null;
        note?: string;
      }
    >
  >;
  [key: string]: any;
};

export const ORDER_COST_GROUPS: OrderCostGroup[] = [
  "estimated",
  "actual",
  "additionalRevenue",
];

export const getAllowedOrderCostReviewStatuses = ({
  canApprove,
  canReject,
}: OrderCostReviewPermissions): OrderCostReviewStatus[] => [
  "PENDING",
  ...(canApprove ? (["APPROVED"] as const) : []),
  ...(canReject ? (["REJECTED", "REQUEST_EDIT"] as const) : []),
];

export const isOrderCostReviewPayloadAllowed = (
  payload: {
    items?: Array<{
      action: Exclude<OrderCostReviewStatus, "PENDING">;
    }>;
  },
  permissions: OrderCostReviewPermissions,
) => {
  const items = payload.items ?? [];

  return (
    items.length > 0 &&
    items.every(({ action }) =>
      action === "APPROVED"
        ? permissions.canApprove
        : permissions.canReject,
    )
  );
};

export const isOrderCostReviewableLine = (line: OrderCostLine) =>
  Boolean(line.isLeaf && line.values?.actual?.value);

export const shouldAdvanceOrderCostReview = (
  tree: OrderCostLine[] = [],
  reviewer: OrderCostReviewer,
) => {
  let hasReviewableLine = false;
  let allApproved = true;

  const walk = (nodes: OrderCostLine[]) => {
    for (const node of nodes) {
      if (isOrderCostReviewableLine(node)) {
        hasReviewableLine = true;
        if (node.reviews?.[reviewer]?.status !== "APPROVED") {
          allApproved = false;
        }
      }

      if (node.children?.length) {
        walk(node.children);
      }
    }
  };

  walk(tree);
  return hasReviewableLine && allApproved;
};

export const setByPath = (
  obj: any,
  path: Array<string | number>,
  value: any,
) => {
  if (!path.length) return obj;

  const [head, ...rest] = path;
  const next = Array.isArray(obj) ? [...obj] : { ...(obj ?? {}) };

  if (!rest.length) {
    next[head] = value;
    return next;
  }

  next[head] = setByPath(next[head], rest, value);
  return next;
};

export const updateTreeById = (
  nodes: OrderCostLine[] = [],
  targetId: string | number,
  updater: (node: OrderCostLine) => OrderCostLine,
): OrderCostLine[] =>
  nodes.map((node) => {
    if (String(node.configId) === String(targetId)) {
      return updater(node);
    }
    if (node.children?.length) {
      return {
        ...node,
        children: updateTreeById(node.children, targetId, updater),
      };
    }
    return node;
  });

export const normalizeDetailFieldValue = (value: any) => {
  if (value && typeof value === "object" && "target" in value) {
    return (value as any).target?.value;
  }
  return value;
};

const hasTouchedGroupField = (line: OrderCostLine, group: OrderCostGroup) =>
  Object.keys(line.touchedFields || {}).some(
    (key) =>
      key === `values.${group}.value` || key.startsWith(`values.${group}.`),
  );

export const mapAllEditableNodesToPayload = (
  tree: OrderCostLine[] = [],
  groups: OrderCostGroup[] = ORDER_COST_GROUPS,
  skipLocked = true,
) => {
  const changes: Array<{
    configId: string;
    group: OrderCostGroup;
    value: number;
    note?: string;
  }> = [];

  const walk = (nodes: OrderCostLine[]) => {
    for (const node of nodes) {
      for (const group of groups) {
        const cell = node.values?.[group];
        if (!cell?.editable) continue;
        if (skipLocked && cell.locked) continue;
        if (!hasTouchedGroupField(node, group)) continue;

        changes.push({
          configId: node.configId,
          group,
          value: Number(cell.value ?? 0),
          note: (node.note ?? "").trim(),
        });
      }

      if (node.children?.length) {
        walk(node.children);
      }
    }
  };

  walk(tree);
  return { changes };
};

export const buildOrderCostReviewPayload = (
  tree: OrderCostLine[] = [],
  reviewer: OrderCostReviewer,
) => {
  const items: Array<{
    configId: string;
    action: "APPROVED" | "REJECTED" | "REQUEST_EDIT";
    note?: string;
  }> = [];

  const walk = (nodes: OrderCostLine[]) => {
    for (const node of nodes) {
      const rawStatus = node.reviews?.[reviewer]?.status;
      if (
        rawStatus &&
        rawStatus !== "PENDING" &&
        (rawStatus === "APPROVED" ||
          rawStatus === "REJECTED" ||
          rawStatus === "REQUEST_EDIT")
      ) {
        items.push({
          configId: node.configId,
          action: rawStatus,
          note: (node.reviews?.[reviewer]?.note ?? "").trim(),
        });
      }

      if (node.children?.length) {
        walk(node.children);
      }
    }
  };

  walk(tree);
  return { items };
};
